import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import { ALL_OPTION } from "./WelcomeSurveySummary";

/// The offices for a report's office dropdown, already shaped as Select
/// options with "All offices" at the head.
///
/// A failed list only costs the dropdown its options — every report still
/// loads on every office, which is what the filter defaults to — so the
/// failure is swallowed rather than surfaced. It is the one request on these
/// pages whose failure leaves the page still worth reading.
export function useOfficeOptions() {
  const { getVerifiedJWT } = useAuth();
  const [offices, setOffices] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadOffices = async () => {
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) throw new Error("Authentication failed");

        const response = await fetch(
          getApiEndpoint("/v1/fieldroutes/offices"),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );
        if (!response.ok) throw new Error("Failed to load offices");

        const result = await response.json();
        const data = result?.data ?? result;
        if (!cancelled) setOffices(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setOffices([]);
      }
    };

    loadOffices();
    return () => {
      cancelled = true;
    };
  }, [getVerifiedJWT]);

  return useMemo(
    () => [
      { value: ALL_OPTION, label: "All offices" },
      ...offices.map(office => ({
        value: String(office.id),
        label: office.name || `Office ${office.fr_office_id ?? office.id}`,
      })),
    ],
    [offices]
  );
}
